import {
	addDays,
	addMonths,
	fromUnixTime,
	getUnixTime,
	isValid
} from 'date-fns';
import {
	AnalyticsTimePeriod,
	IAnalytic,
	IAnalyticData,
	IWidgetItem
} from 'types/analytics';

export const BAR_WIDGET_HEIGHT = 350;
export const WIDGET_SMALL_HEIGHT = 150;
export const WIDGET_LARGE_HEIGHT = 250;

export const LEGEND_GLYPH_SIZE = 12;

export const Y_AXIS_MAX_TICKS = 10;
export const X_AXIS_MAX_TICKS = 12;

export const PIE_LABEL_MINIMUM_ANGLE = 0.25;

export const countAnalyticsFormat = (data: IAnalyticData) =>
	(data.analytics || []).map((item: IAnalytic) => ({
		count: item.cnt || 0,
		name: (item?.cnt || 0).toString() || '',
		date: item.created || 0
	}));

export const fieldAnalyticsFormat = (
	field: keyof IAnalytic,
	data: IAnalyticData
) =>
	(data.analytics || []).map((item: IAnalytic) => ({
		count: item.cnt || 0,
		name: (item[field] as string) || '',
		date: item.created || 0
	}));

export const numericDateConvertor = (date: number) =>
	getUnixTime(new Date(fromUnixTime(date / 1000).toDateString())) * 1000;

export const numericMonthConvertor = (date: number) => {
	const d = fromUnixTime(date / 1000);
	d.setDate(1); // normalize day
	d.setHours(0, 0, 0, 0); // normalize time
	return getUnixTime(d) * 1000;
};

export const dateAccessor = (item: Partial<IWidgetItem>) =>
	numericDateConvertor(item.date || 0);
export const dateMonthAccessor = (item: Partial<IWidgetItem>) =>
	numericMonthConvertor(item.date || 0);

export const getDateAccessor = (timePeriod: `${AnalyticsTimePeriod}`) => {
	switch (timePeriod) {
		case AnalyticsTimePeriod.SixMonths:
			return dateMonthAccessor;
		default:
			return dateAccessor;
	}
};

export const countAccessor = (item: Partial<IWidgetItem>) => item.count || 0;
export const nameAccessor = (item: Partial<IWidgetItem>) => item.name || '';

export const pieSortValues = (a: number, b: number) => b - a;

export const tickLabelFormat =
	(timePeriod: `${AnalyticsTimePeriod}`) => (time: any) => {
		const date = fromUnixTime(time / 1000);

		if (isValid(date)) {
			return intl.formatDate(date, {
				month: 'short',
				day:
					timePeriod === AnalyticsTimePeriod.SixMonths ? undefined : 'numeric'
			});
		}
		return intl.tr('app.na');
	};

export const analyticsNumericFormat = (count: number) =>
	Intl.NumberFormat('en-US', {
		notation: 'compact',
		maximumFractionDigits: 1
	}).format(count);

export const setXDomainAccordingToTimePeriod = (
	analyticsTimePeriod?: AnalyticsTimePeriod
) => {
	const now = new Date();
	const times: Date[] = [now];
	switch (analyticsTimePeriod) {
		case AnalyticsTimePeriod.Month:
			// push every day of the month
			for (let i = 1; i <= 30; i += 1) {
				times.push(addDays(now, -i));
			}
			break;
		case AnalyticsTimePeriod.SixMonths:
			// add every month
			for (let i = 1; i <= 6; i += 1) {
				times.push(addMonths(now, -i));
			}

			break;
		default:
			// add all days of the week
			for (let i = 1; i <= 6; i += 1) {
				times.push(addDays(now, -i));
			}
			break;
	}

	// times 1000 because of milliseconds
	return times.map((time) => getUnixTime(time) * 1000);
};
